import {
  REPORT_TREE_DISPLAY_CONFIG,
  type ReportTreeDisplayRules,
} from "@/config/report-tree-display"
import type { ReportResponse } from "@/types/report"

/** The distinct report categories shown as tabs in the Report Library */
export type ReportType = "project" | "administrative" | "custom"

/** Path prefix used to route a report to its type/tab and to build its folder tree */
export const REPORT_TYPE_CONFIG: Record<ReportType, { prefix: string }> = {
  project: { prefix: "CSReportingTelerik.Reports." },
  administrative: { prefix: "CSReportingTelerik.ReportsMultiProject." },
  custom: { prefix: "CSReportingTelerik.ReportsCustom." },
}

/** Display/tab order for report types */
export const REPORT_TYPE_ORDER: ReportType[] = ["project", "administrative", "custom"]

/** Extended TreeItemData for report tree nodes */
export interface ReportTreeItemData {
  id: string
  name: string
  type: "file" | "folder"
  children?: ReportTreeItemData[]
  /** Only on folder nodes — original dot-path (prefix stripped), key for display config lookups */
  folderPath?: string
  /** Only on report (file) nodes */
  reportKey?: string
  /** Only on report (file) nodes — canonical report name for thumbnail API */
  reportName?: string
  /** Only on report (file) nodes — full report path for API calls */
  path?: string
  /** Only on report (file) nodes — description for details panel */
  description?: string
  /** Only on report (file) nodes — version string */
  reportVersion?: string
  /** Index signature for TreeItemData compatibility */
  [key: string]: unknown
}

export function buildReportTree(
  reports: ReportResponse[],
  reportType: ReportType = "project",
): ReportTreeItemData[] {
  const root: ReportTreeItemData[] = []
  const prefix = REPORT_TYPE_CONFIG[reportType].prefix

  // Only include reports whose path matches this type's prefix and are active (no inactiveDateTime)
  const filteredReports = reports.filter(
    (report) => report.path.startsWith(prefix) && !report.inactiveDateTime,
  )

  for (const report of filteredReports) {
    // Strip the prefix from the report path
    const cleanPath = report.path.slice(prefix.length)

    const parts = cleanPath.split(".")
    let currentLevel = root

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      const isLastPart = i === parts.length - 1
      const pathSoFar = parts.slice(0, i + 1).join(".")

      if (isLastPart) {
        // This is a report leaf node (type: "file" for TreeItemData compatibility)
        currentLevel.push({
          id: report.reportKey,
          name: report.displayName || report.name,
          type: "file",
          reportKey: report.reportKey,
          reportName: report.name,
          path: report.path,
          description: report.description,
          reportVersion: report.reportVersion,
        })
      } else {
        // This is a folder — find or create
        let folder = currentLevel.find(
          (n) => n.type === "folder" && n.name === part,
        )
        if (!folder) {
          folder = {
            id: `folder-${pathSoFar}`,
            name: part,
            type: "folder",
            folderPath: pathSoFar,
            children: [],
          }
          currentLevel.push(folder)
        }
        currentLevel = folder.children!
      }
    }
  }

  return applyTreeDisplayConfig(sortTree(root), REPORT_TREE_DISPLAY_CONFIG[reportType])
}

/** Paths already reported as unmatched, so the dev warning fires once per path. */
const warnedDisplayPaths = new Set<string>()

/**
 * Applies display-only folder promotion and renaming. Node IDs, report fields and ordering
 * semantics are preserved so persisted expansion state and report behavior are unaffected.
 */
export function applyTreeDisplayConfig(
  nodes: ReportTreeItemData[],
  rules: ReportTreeDisplayRules,
): ReportTreeItemData[] {
  const promote = new Set(rules.promote ?? [])
  const rename = rules.rename ?? {}
  const renameKeys = Object.keys(rename)
  if (promote.size === 0 && renameKeys.length === 0) return nodes

  const matched = new Set<string>()

  const transform = (level: ReportTreeItemData[]): ReportTreeItemData[] => {
    const result: ReportTreeItemData[] = []

    for (const node of level) {
      if (node.type !== "folder") {
        result.push(node)
        continue
      }

      const children = node.children ? transform(node.children) : undefined
      const path = node.folderPath

      if (path !== undefined && promote.has(path)) {
        matched.add(path)
        result.push(...(children ?? []))
        continue
      }

      const renamed = path !== undefined ? rename[path] : undefined
      if (path !== undefined && renamed !== undefined) matched.add(path)

      result.push({ ...node, name: renamed ?? node.name, children })
    }

    return sortLevel(result)
  }

  const transformed = transform(nodes)

  if (import.meta.env.DEV) {
    const unmatched = [...promote, ...renameKeys].filter(
      (path) => !matched.has(path) && !warnedDisplayPaths.has(path),
    )
    if (unmatched.length > 0) {
      unmatched.forEach((path) => warnedDisplayPaths.add(path))
      console.warn(
        `[report-tree] Unmatched display config folder paths: ${unmatched.join(", ")}`,
      )
    }
  }

  return transformed
}

/** Build a separate report tree for each report type/tab */
export function buildReportTreesByType(
  reports: ReportResponse[],
): Record<ReportType, ReportTreeItemData[]> {
  return REPORT_TYPE_ORDER.reduce(
    (acc, reportType) => {
      acc[reportType] = buildReportTree(reports, reportType)
      return acc
    },
    {} as Record<ReportType, ReportTreeItemData[]>,
  )
}

/** Which report types have at least one report, in tab display order */
export function getReportTypesWithReports(
  treesByType: Record<ReportType, ReportTreeItemData[]>,
): ReportType[] {
  return REPORT_TYPE_ORDER.filter(
    (reportType) => treesByType[reportType].length > 0,
  )
}

/**
 * Normalizes persisted expanded-folder state into the per-type shape.
 * Handles the legacy single-array format (pre-tabs), treating it as the Project tab's state.
 */
export function normalizeExpandedFoldersByType(
  value: string[] | Partial<Record<ReportType, string[]>> | null | undefined,
): Record<ReportType, string[]> {
  if (Array.isArray(value)) {
    return { project: value, administrative: [], custom: [] }
  }
  return {
    project: value?.project ?? [],
    administrative: value?.administrative ?? [],
    custom: value?.custom ?? [],
  }
}

/** Sort a single level: folders first, then reports, each alphabetical by display name */
function sortLevel(nodes: ReportTreeItemData[]): ReportTreeItemData[] {
  return [...nodes].sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1
    return a.name.localeCompare(b.name)
  })
}

function sortTree(nodes: ReportTreeItemData[]): ReportTreeItemData[] {
  return sortLevel(nodes).map((node) => ({
    ...node,
    children: node.children ? sortTree(node.children) : undefined,
  }))
}

/** Count reports in a folder (recursively) */
export function countReportsInFolder(node: ReportTreeItemData): number {
  if (node.type === "file") return 1
  return (node.children || []).reduce(
    (sum, child) => sum + countReportsInFolder(child),
    0,
  )
}

/** Flatten tree to get all report nodes */
export function flattenReportNodes(nodes: ReportTreeItemData[]): ReportTreeItemData[] {
  const result: ReportTreeItemData[] = []
  for (const node of nodes) {
    if (node.type === "file") {
      result.push(node)
    }
    if (node.children) {
      result.push(...flattenReportNodes(node.children))
    }
  }
  return result
}

/** Get all folder IDs (for expand all/collapse all) */
export function getAllFolderIds(nodes: ReportTreeItemData[]): string[] {
  const ids: string[] = []
  for (const node of nodes) {
    if (node.type === "folder") {
      ids.push(node.id)
      if (node.children) {
        ids.push(...getAllFolderIds(node.children))
      }
    }
  }
  return ids
}
